import React, { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaCheck, FaLink, FaSpinner } from "react-icons/fa";

import { fetchAssetCompanyMappings, fetchClients, saveAssetCompanyMapping } from "../../api/overviewApi";

const ACCENT = "#6366f1";

/**
 * Staff-only panel for ConnectWise companies that still need a OneView client link.
 *
 * Only companies with devices are listed. Zero-device shells and internal Versetal
 * labs are filtered out by the API — mapping them would not change the Assets page.
 */
const AssetMappingPanel = ({ defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [clients, setClients] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);

  useEffect(() => {
    // Always load once so we know whether this panel has anything useful to show.
    if (loaded) return;

    let cancelled = false;
    setLoading(true);

    Promise.all([fetchAssetCompanyMappings(), fetchClients()])
      .then(([mappingResult, clientList]) => {
        if (cancelled) return;
        setMappings(mappingResult?.mappings || []);
        setClients(clientList || []);
        setLoaded(true);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loaded]);

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => (a.client_name || "").localeCompare(b.client_name || "")),
    [clients]
  );

  const unmapped = useMemo(
    () => mappings.filter((mapping) => !mapping.client_id),
    [mappings]
  );

  const handleAssign = async (cwCompanyId, clientId) => {
    setSavingId(cwCompanyId);
    const result = await saveAssetCompanyMapping(cwCompanyId, clientId || null);
    setSavingId(null);

    if (result?.success) {
      const client = sortedClients.find((candidate) => String(candidate.client_id) === String(clientId));
      setMappings((prev) =>
        prev.map((mapping) =>
          mapping.cw_company_id === cwCompanyId
            ? {
                ...mapping,
                client_id: clientId ? Number(clientId) : null,
                client_name: client?.client_name || null,
                mapping_source: "manual",
                mapping_confirmed: true,
              }
            : mapping
        )
      );
      setSavedId(cwCompanyId);
      setTimeout(() => setSavedId((current) => (current === cwCompanyId ? null : current)), 2200);
    }
  };

  // Nothing left to link — the panel has no job on this page.
  if (loaded && unmapped.length === 0) {
    return null;
  }

  return (
    <Card
      style={{
        borderRadius: 16,
        background: "var(--color-bg-card)",
        border: "1px solid var(--color-border)",
        boxShadow: "var(--shadow-sm)",
      }}
    >
      <Card.Body className="p-3">
        <div
          onClick={() => setOpen((prev) => !prev)}
          className="d-flex align-items-center justify-content-between"
          style={{ cursor: "pointer", gap: 12 }}
        >
          <div className="d-flex align-items-center gap-2">
            <div style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(99,102,241,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <FaLink size={14} color={ACCENT} />
            </div>
            <div>
              <h6 className="fw-bold m-0" style={{ color: "var(--color-text)" }}>ConnectWise Company Mapping</h6>
              <span style={{ fontSize: 11, color: "var(--color-text-light)" }}>
                Link these companies so their devices appear under the right OneView client
              </span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-3">
            {(loaded || loading) && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#f59e0b",
                  background: "rgba(245,158,11,0.12)",
                  padding: "4px 10px",
                  borderRadius: 10,
                }}
              >
                {loaded ? `${unmapped.length} need mapping` : "…"}
              </span>
            )}
            <FaChevronDown
              size={11}
              color="var(--color-text-light)"
              style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
            />
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: "hidden" }}
            >
              <div style={{ borderTop: "1px solid var(--color-border)", marginTop: 14, paddingTop: 14 }}>
                {loading || !loaded ? (
                  <div className="d-flex align-items-center gap-2" style={{ color: "var(--color-text-light)", fontSize: 13 }}>
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}>
                      <FaSpinner size={12} />
                    </motion.div>
                    Loading companies that need mapping...
                  </div>
                ) : (
                  <div style={{ maxHeight: 340, overflowY: "auto" }}>
                    {unmapped.map((mapping) => (
                      <div
                        key={mapping.cw_company_id}
                        className="d-flex align-items-center justify-content-between"
                        style={{ gap: 12, padding: "10px 0", borderBottom: "1px solid var(--color-border)", flexWrap: "wrap" }}
                      >
                        <div style={{ minWidth: 200 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text)" }}>
                            {mapping.cw_company_name}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--color-text-light)" }}>
                            {mapping.device_count.toLocaleString()} {mapping.device_count === 1 ? "device" : "devices"} waiting to be linked
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          {savedId === mapping.cw_company_id && <FaCheck size={12} color="#10b981" />}
                          {savingId === mapping.cw_company_id && (
                            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
                              <FaSpinner size={12} color={ACCENT} />
                            </motion.div>
                          )}
                          <select
                            value={mapping.client_id || ""}
                            onChange={(event) => handleAssign(mapping.cw_company_id, event.target.value)}
                            disabled={savingId === mapping.cw_company_id}
                            style={{
                              borderRadius: 10,
                              background: "var(--color-bg-card)",
                              border: "1px solid var(--color-border)",
                              color: "var(--color-text)",
                              padding: "6px 10px",
                              fontSize: 12,
                              minWidth: 220,
                              cursor: "pointer",
                            }}
                          >
                            <option value="">Select OneView client…</option>
                            {sortedClients.map((client) => (
                              <option key={client.client_id} value={client.client_id}>
                                {client.client_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card.Body>
    </Card>
  );
};

export default AssetMappingPanel;
